// SubjectsSection.jsx
import "../../../../../design/subjectsSection.css";
import { useEffect, useState } from "react";
import api from "../../../../../api/API_SERVERS";
import Loading from "../../../../../utils/components/Loading";
import { FaTrashCan } from "react-icons/fa6";

export default function SubjectsSection({ showToast }) {
    const [subjects, setSubjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectOpen, setSelectOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [search, setSearch] = useState("");
    const [isEditing, setIsEditing] = useState(false);

    const [newSubject, setNewSubject] = useState({
        name: "",
        experience: "",
        level: "",
        price: "",
        lesson_location: false
    });

    const [originalSubject, setOriginalSubject] = useState(null);

    const subjectOptions = [
        "გერმანული", "ფრანგული", "ინგლისური", "ისტორია", "მათემატიკა", "მუსიკა",
        "სამოქალაქო", "ბიოლოგია", "გეოგრაფია", "პროგრამირება", "აიტი", "რუსული",
        "ქართული", "ბუნება", "ჭადრაკი", "ხელოვნება", "ფიზიკა", "ქიმია", "იტალიური"
    ];

    const experienceOptions = ["0-1", "2-5", "6-10", "10+"];
    const levelOptions = [
        "დაწყებითები", "საშუალო", "უმაღლესი",
        "დაწყებითები+საშუალო", "დაწყებითები+უმაღლესი",
        "საშუალო+უმაღლესი", "ყველა"
    ];

    const filteredSubjects = subjectOptions.filter(s =>
        s.toLowerCase().includes(search.toLowerCase())
    );

    const formatClassName = (text) => {
        if (!text) return "";
        return text
            .toString()
            .split(' ')[0]
            .toLowerCase()
            .trim()
            .replace(/\+/g, '-plus');
    };

    const getSubjects = async () => {
        try {
            const res = await api.get("/api/teachers/profile/subjects");
            setSubjects(res.data.subjects || []);
        } catch {
            showToast("საგნების მიღება ვერ მოხერხდა ❌");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getSubjects();
        // eslint-disable-next-line
    }, []);

    const openAddModal = () => {
        setIsEditing(false);
        const empty = { name: "", experience: "", level: "", price: "", lesson_location: false };
        setNewSubject(empty);
        setOriginalSubject(empty);
        setModalOpen(true);
    };

    const openEditModal = (subject) => {
        setIsEditing(true);
        const data = { ...subject };
        setNewSubject(data);
        setOriginalSubject(data);
        setModalOpen(true);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setNewSubject(prev => ({ ...prev, [name]: value }));
    };

    const isUnchanged = isEditing && originalSubject &&
        newSubject.experience === originalSubject.experience &&
        newSubject.level === originalSubject.level &&
        newSubject.lesson_location === originalSubject.lesson_location &&
        Number(newSubject.price) === Number(originalSubject.price);

    const handleSubmit = async () => {
        const { name, experience, level, price } = newSubject;

        if (!name || !experience || !level || !price) {
            showToast("ყველა ველი სავალდებულოა ❗");
            return;
        }

        try {
            setSubmitting(true);
            if (isEditing) {
                await api.put("/api/teachers/profile/update/subject", newSubject);
                showToast("მონაცემები განახლდა ✅", "success");
            } else {
                await api.post("/api/teachers/profile/subjects/add", newSubject);
                showToast("საგანი წარმატებით დაემატა ✅", "success");
            }

            await getSubjects();
            setModalOpen(false);
            setSelectOpen(false);
            setSearch("");
        } catch (error) {
            showToast(error.response?.data?.message || "შეცდომა ❌");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (subjectName) => {
        if (!window.confirm(`ნამდვილად გსურთ ${subjectName}-ის წაშლა?`)) return;

        try {
            setLoading(true);
            await api.delete("/api/teachers/profile/delete/subject", {
                data: { name: subjectName }
            });
            showToast("საგანი წარმატებით წაიშალა ✅", "success");
            await getSubjects();
        } catch (error) {
            showToast(error.response?.data?.message || "წაშლა ვერ მოხერხდა ❌");
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <Loading />;

    return (
        <div className="subjects-space">
            <div className="subjects-header">
                <h4 className="subjects-title">საგნები</h4>
                <button className="btn-add" onClick={openAddModal}>+ დამატება</button>
            </div>

            <div className="subjects-grid">
                {subjects.map((sub, i) => (
                    <div className="profile-subject-card" key={i}>
                        <div className="card-top">
                            <h3 className="subject-name">{sub.name}</h3>
                            <button className="btn-edit-icon" onClick={() => openEditModal(sub)}>✎</button>
                        </div>

                        <div className="profile-subject-details">
                            <div className="info-row">
                                <span className="info-label">გამოცდილება:</span>
                                <span className={`profile-info-badge profile-exp-${formatClassName(sub.experience)}`}>
                                    {sub.experience} წელი
                                </span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">დონე:</span>
                                <span className={`profile-info-badge profile-lvl-${formatClassName(sub.level)}`}>
                                    {sub.level}
                                </span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">ლოკაცია:</span>
                                <span className={`profile-info-badge profile-loc-${sub.lesson_location ? "student" : "teacher"}`}>
                                    {sub.lesson_location ? "🏠 მოწაფესთან" : "🏫 მასწავლებელთან"}
                                </span>
                            </div>
                        </div>

                        <div className="subject-footer">
                            <span className="price-text">{sub.price} ₾</span>
                            <span className="price-unit">/ საათი</span>
                        </div>

                        <button
                            className="btn-delete-subject"
                            onClick={() => handleDelete(sub.name)}
                        >
                            <FaTrashCan />
                            <span>წაშლა</span>
                        </button>
                    </div>
                ))}
            </div>

            {modalOpen && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3 className="modal-title">{isEditing ? "რედაქტირება" : "საგნის დამატება"}</h3>

                        <div className="field-group">
                            <label className="field-label">საგანი</label>
                            <div className={`custom-select ${isEditing ? "disabled-select" : ""}`}>
                                <div className="select-header" onClick={() => !isEditing && setSelectOpen(!selectOpen)}>
                                    {newSubject.name || "აირჩიეთ საგანი"}
                                </div>
                                {selectOpen && !isEditing && (
                                    <div className="select-dropdown">
                                        <input
                                            className="select-search"
                                            placeholder="ძიება..."
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                        />
                                        <div className="select-options">
                                            {filteredSubjects.map((s, i) => {
                                                const used = subjects.some(sub => sub.name === s);
                                                return (
                                                    <div
                                                        key={i}
                                                        className={`option-item ${used ? "option-used" : ""}`}
                                                        onClick={() => {
                                                            if (used) return;
                                                            setNewSubject(prev => ({ ...prev, name: s }));
                                                            setSearch("");
                                                            setSelectOpen(false);
                                                        }}
                                                    >
                                                        {s}
                                                        {used && <span className="used-tag">უკვე გაქვთ</span>}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="field-group">
                            <label className="field-label">გამოცდილება</label>
                            <select className="field-select" name="experience" value={newSubject.experience} onChange={handleChange}>
                                <option value=""></option>
                                {experienceOptions.map((e, i) => (
                                    <option key={i} value={e}>{e}</option>
                                ))}
                            </select>
                        </div>

                        <div className="field-group">
                            <label className="field-label">დონე</label>
                            <select className="field-select" name="level" value={newSubject.level} onChange={handleChange}>
                                <option value=""></option>
                                {levelOptions.map((l, i) => (
                                    <option key={i} value={l}>{l}</option>
                                ))}
                            </select>
                        </div>

                        <div className="field-group">
                            <label className="field-label">ფასი (₾)</label>
                            <input className="field-input" type="number" name="price" value={newSubject.price} onChange={handleChange} min="10" max="100" />
                        </div>

                        <div className="field-group">
                            <label className="profile-subject-location-toggle-label">
                                <input
                                    type="checkbox"
                                    className="profile-subject-location-checkbox"
                                    checked={newSubject.lesson_location || false}
                                    onChange={(e) => setNewSubject(prev => ({ ...prev, lesson_location: e.target.checked }))}
                                />
                                <span>მე მივალ მოსწავლესთან</span>
                            </label>
                        </div>

                        <div className="modal-actions">
                            <button className="btn-cancel" onClick={() => setModalOpen(false)}>გაუქმება</button>
                            <button
                                className={`btn-confirm ${isUnchanged ? "btn-disabled" : ""}`}
                                onClick={handleSubmit}
                                disabled={submitting || isUnchanged}
                            >
                                {submitting ? "..." : isEditing ? "შენახვა" : "დამატება"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}